<template>
  <div class="dashboard-container">
    <div class="dashboard-header">
      <div class="title-section">
        <h2 class="dashboard-subtitle">GTO Settings</h2>
      </div>
    </div>

    <div class="tabs">
      <button 
        class="tab-button" 
        :class="{ active: activeTab === 'settings' }"
        @click="activeTab = 'settings'"
      >
        Settings
      </button>
      <button 
        class="tab-button" 
        :class="{ active: activeTab === 'historical', 'disabled': !settingsExist }"
        @click="activeTab = 'historical'"
        :disabled="!settingsExist"
      >
        Historical
      </button>
    </div>

    <!-- Settings Tab -->
    <div v-if="activeTab === 'settings'" class="dashboard-card">
      <div class="card-header">
        <div class="header-left">
          <select 
            id="project_name" 
            v-model="formData.project_name" 
            :class="{ 'error': errors.project_name, 'disabled': settingsExist }"
            class="project-select"
            required
            :disabled="settingsExist"
          >
            <option value="">Select Project Type</option>
            <option value="capitaland">Capitaland</option>
            <option value="maple-tree">Maple Tree</option>
            <option value="hougang-mall">Hougang Mall</option>
            <option value="amk-hub">AMK Hub</option>
            <option value="smrt-alpha">SMRT Alpha</option>
          </select>
        </div>
        <div class="header-right">
          <button type="button" class="action-button outline" @click="loadSettings" :disabled="isLoading">
            Load Settings
          </button>
          <button type="button" class="action-button primary" @click="handleSubmit" :disabled="isLoading">
            Save Settings
          </button>
        </div>
      </div>

      <form @submit.prevent="handleSubmit" class="settings-form">
        <div class="form-row">
          <div class="form-group">
            <label for="username">Username</label>
            <input 
              type="text" 
              id="username" 
              v-model="formData.username" 
              :class="{ 'error': errors.username }"
              placeholder="Enter username"
              required
            />
          </div>

          <div class="form-group">
            <label for="password">Password</label>
            <div class="password-input-container">
              <input 
                :type="showPassword ? 'text' : 'password'"
                id="password" 
                v-model="formData.password" 
                :class="{ 'error': errors.password }"
                placeholder="Enter password"
                required
              />
              <button 
                type="button" 
                class="toggle-password"
                @click="showPassword = !showPassword"
                :title="showPassword ? 'Hide password' : 'Show password'"
              >
                <span v-if="showPassword">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                </span>
                <span v-else>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="host">Host</label>
            <input 
              type="text" 
              id="host" 
              v-model="formData.host" 
              :class="{ 'error': errors.host }"
              placeholder="Enter host"
              required
            />
          </div>

          <div class="form-group">
            <label for="port">Port</label>
            <input 
              type="text" 
              id="port" 
              v-model="formData.port" 
              :class="{ 'error': errors.port }"
              placeholder="Enter port"
              required
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="folder_path">Folder Path</label>
            <input 
              type="text" 
              id="folder_path" 
              v-model="formData.folder_path" 
              :class="{ 'error': errors.folder_path }"
              placeholder="Enter folder path"
              required
            />
          </div>

          <div class="form-group">
            <label for="machine_id">Machine ID</label>
            <input 
              type="text" 
              id="machine_id" 
              v-model="formData.machine_id" 
              :class="{ 'error': errors.machine_id }"
              placeholder="Enter machine ID"
              required
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="is_ftp">Connection Type</label>
            <div class="ftp-container">
              <select 
                id="is_ftp" 
                v-model="formData.is_ftp" 
                :class="{ 'error': errors.is_ftp }"
                required
              >
                <option value="0">SFTP</option>
                <option value="1">FTP</option>
              </select>
              <button 
                type="button" 
                class="action-button outline verify-button"
                @click="verifyFtpCredentials"
                :disabled="isVerifying"
              >
                <span v-if="isVerifying">Verifying...</span>
                <span v-else>Verify Connection</span>
              </button>
            </div>
          </div>

          <div class="form-group">
            <label for="start_date">Start Date</label>
            <input 
              type="date" 
              id="start_date" 
              v-model="formData.start_date" 
              :class="{ 'error': errors.start_date }"
              required
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="to_email">To Email</label>
            <div class="email-tags">
              <div v-for="(email, index) in toEmails" :key="index" class="email-tag">
                {{ email }}
                <button type="button" @click="removeToEmail(index)" class="remove-tag">&times;</button>
              </div>
              <input
                type="text"
                id="to_email"
                v-model="emailInput"
                @keydown.enter.prevent="addToEmail"
                @blur="addToEmail"
                placeholder="Type email and press Enter"
                :class="{ 'error': errors.to_email }"
              />
            </div>
            <span class="error-message" v-if="errors.to_email">{{ errors.to_email }}</span>
          </div>

          <div class="form-group">
            <label for="bcc_email">BCC Email</label>
            <div class="email-tags">
              <div v-for="(email, index) in bccEmails" :key="index" class="email-tag">
                {{ email }}
                <button type="button" @click="removeBccEmail(index)" class="remove-tag">&times;</button>
              </div>
              <input
                type="text"
                id="bcc_email"
                v-model="bccEmailInput"
                @keydown.enter.prevent="addBccEmail"
                @blur="addBccEmail"
                placeholder="Type email and press Enter"
                :class="{ 'error': errors.bcc_email }"
              />
            </div>
            <span class="error-message" v-if="errors.bcc_email">{{ errors.bcc_email }}</span>
          </div>
        </div>
      </form>
    </div>

    <!-- Historical Tab -->
    <div v-if="activeTab === 'historical'" class="dashboard-card">
      <div class="card-header">
        <button type="button" class="action-button outline" @click="createDailyEvent" :disabled="isLoading">
          Create Daily Event
        </button>
        <button type="button" class="action-button outline" @click="createHistoricalEvent" :disabled="isLoading">
          Create Historical Event
        </button>
      </div>

      <form @submit.prevent="sendFiles" class="historical-form">
        <div class="form-row">
          <div class="form-group">
            <label for="historical_start_date">Start Date</label>
            <input 
              type="date" 
              id="historical_start_date" 
              v-model="historicalData.start_date" 
              required
            />
          </div>

          <div class="form-group">
            <label for="historical_end_date">End Date</label>
            <input 
              type="date" 
              id="historical_end_date" 
              v-model="historicalData.end_date" 
              required
            />
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="action-button primary" :disabled="isLoading">
            <span v-if="isLoading">Processing...</span>
            <span v-else>Send Historical Data</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import config from '../../config'

const router = useRouter()
const isLoading = ref(false)
const activeTab = ref('settings')
const emailInput = ref('')
const bccEmailInput = ref('')
const toEmails = ref([])
const bccEmails = ref([])
const showPassword = ref(false)
const isVerifying = ref(false)
const settingsExist = ref(false)
const success = ref('')

const historicalData = ref({
  project_name: '',
  start_date: '',
  end_date: ''
})

const formData = ref({
  username: '',
  password: '',
  host: '',
  port: '22',
  folder_path: '/',
  machine_id: '',
  to_email: '',
  start_date: '',
  is_ftp: '0',
  bcc_email: '',
  project_name: ''
})

const errors = ref({
  username: '',
  password: '',
  host: '',
  port: '',
  folder_path: '',
  machine_id: '',
  to_email: '',
  start_date: '',
  is_ftp: '',
  bcc_email: '',
  project_name: ''
})

const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(email)
}

const addToEmail = () => {
  if (emailInput.value && validateEmail(emailInput.value) && !toEmails.value.includes(emailInput.value)) {
    toEmails.value.push(emailInput.value)
    formData.value.to_email = toEmails.value.join(',')
    emailInput.value = ''
  }
}

const removeToEmail = (index) => {
  toEmails.value.splice(index, 1)
  formData.value.to_email = toEmails.value.join(',')
}

const handleBackspace = (e) => {
  if (e.target.value === '' && toEmails.value.length > 0) {
    toEmails.value.pop()
    formData.value.to_email = toEmails.value.join(',')
  }
}

const addBccEmail = () => {
  if (bccEmailInput.value && validateEmail(bccEmailInput.value) && !bccEmails.value.includes(bccEmailInput.value)) {
    bccEmails.value.push(bccEmailInput.value)
    formData.value.bcc_email = bccEmails.value.join(',')
    bccEmailInput.value = ''
  }
}

const removeBccEmail = (index) => {
  bccEmails.value.splice(index, 1)
  formData.value.bcc_email = bccEmails.value.join(',')
}

const handleBccBackspace = (e) => {
  if (e.target.value === '' && bccEmails.value.length > 0) {
    bccEmails.value.pop()
    formData.value.bcc_email = bccEmails.value.join(',')
  }
}

const validateForm = () => {
  let isValid = true
  errors.value = {
    username: '',
    password: '',
    host: '',
    port: '',
    folder_path: '',
    machine_id: '',
    to_email: '',
    start_date: '',
    is_ftp: '',
    bcc_email: '',
    project_name: ''
  }

  if (!formData.value.username) {
    errors.value.username = 'Username is required'
    isValid = false
  }

  if (!formData.value.password) {
    errors.value.password = 'Password is required'
    isValid = false
  }

  if (!formData.value.host) {
    errors.value.host = 'Host is required'
    isValid = false
  }

  if (!formData.value.port) {
    errors.value.port = 'Port is required'
    isValid = false
  }

  if (!formData.value.folder_path) {
    errors.value.folder_path = 'Folder path is required'
    isValid = false
  }

  if (!formData.value.machine_id) {
    errors.value.machine_id = 'Machine ID is required'
    isValid = false
  }

  if (toEmails.value.length === 0) {
    errors.value.to_email = 'At least one email is required'
    isValid = false
  }

  if (!formData.value.start_date) {
    errors.value.start_date = 'Start date is required'
    isValid = false
  }

  return isValid
}

const getAuthHeader = () => {
  const token = localStorage.getItem('token')
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }
}

const handleSubmit = async () => {
  if (!validateForm()) return

  isLoading.value = true
  try {
    const response = await axios.post(`${config.serverUrl}/api/gto/save-setting`, {
      MachineId: formData.value.machine_id,
      Username: formData.value.username,
      Password: formData.value.password,
      Host: formData.value.host,
      Port: formData.value.port,
      FolderPath: formData.value.folder_path,
      ToEmail: formData.value.to_email,
      StartDate: formData.value.start_date,
      IsFtp: formData.value.is_ftp,
      BccEmail: formData.value.bcc_email,
      ProjectName: formData.value.project_name
    }, {
      headers: getAuthHeader()
    })
    
    if (response.data.success) {
      success.value = 'Settings saved successfully'
      settingsExist.value = true
      // Clear any existing errors
      errors.value = {
        username: '',
        password: '',
        host: '',
        port: '',
        folder_path: '',
        machine_id: '',
        to_email: '',
        start_date: '',
        is_ftp: '',
        bcc_email: '',
        project_name: ''
      }
      alert('Settings saved successfully')
    } else {
      throw new Error(response.data.message || 'Failed to save settings')
    }
  } catch (error) {
    console.error('Error:', error)
    errors.value.machine_id = error.response?.data?.message || 'Failed to save settings'
    alert(error.response?.data?.message || 'Failed to save settings')
  } finally {
    isLoading.value = false
  }
}

const createDailyEvent = async () => {

  isLoading.value = true
  try {
    const response = await axios.post(`${config.serverUrl}/api/gto/create-event`, {
      templateName: formData.value.project_name,
      eventType: 'Daily'
    }, {
      headers: getAuthHeader()
    })
    
    if (response.data.success) {
      alert('Daily event created successfully')
    } else {
      throw new Error(response.data.message || 'Failed to create daily event')
    }
  } catch (error) {
    console.error('Error:', error)
    alert(error.response?.data?.message || 'Failed to create daily event')
  } finally {
    isLoading.value = false
  }
}

const createHistoricalEvent = async () => {


  isLoading.value = true
  try {
    const response = await axios.post(`${config.serverUrl}/api/gto/create-event`, {
      templateName: formData.value.project_name,
      eventType: 'Historical'
    }, {
      headers: getAuthHeader()
    })
    
    if (response.data.success) {
      alert('Historical event created successfully')
    } else {
      throw new Error(response.data.message || 'Failed to create historical event')
    }
  } catch (error) {
    console.error('Error:', error)
    alert(error.response?.data?.message || 'Failed to create historical event')
  } finally {
    isLoading.value = false
  }
}

const sendFiles = async () => {
  if (!historicalData.value.start_date || !historicalData.value.end_date) {
    alert('Please select both start and end dates')
    return
  }

  isLoading.value = true
  try {
    const response = await axios.post(`${config.serverUrl}/api/gto/send-files`, {
      start_date: historicalData.value.start_date,
      end_date: historicalData.value.end_date,
      project_name: formData.value.project_name
    }, {
      headers: getAuthHeader()
    })
    
    if (response.data.success) {
      alert('Historical data sent successfully')
    } else {
      throw new Error(response.data.message || 'Failed to send historical data')
    }
  } catch (error) {
    console.error('Error:', error)
    alert(error.response?.data?.message || 'Failed to send historical data')
  } finally {
    isLoading.value = false
  }
}

const loadSettings = async () => {
  try {
    console.log('Starting loadSettings function...')
    console.log('Current machine ID:', formData.value.machine_id)
    

    isLoading.value = true
    errors.value.machine_id = ''
    
    console.log('Making API call to get-secret endpoint with machine ID:', formData.value.machine_id)
    const response = await axios.post(`${config.serverUrl}/api/gto/get-setting`, {
    }, {
      headers: getAuthHeader()
    })
    
    console.log('API Response:', response.data)
    
    if (response.data.success) {
      const settings = response.data.secretValues
      console.log('Retrieved settings:', settings)
      
      // Update form data with the settings
      formData.value = {
        ...formData.value,
        username: settings.username || '',
        password: settings.password || '',
        host: settings.host || '',
        port: settings.port || '22',
        folder_path: settings.folderPath || '/',
        to_email: settings.toEmail || '',
        machine_id: settings.machineId,
        start_date: settings.startDate ? new Date(settings.startDate).toISOString().split('T')[0] : '',
        is_ftp: '0', // Default value
        bcc_email: settings.bccEmail || '',
        project_name: response.data.templateName || ''
      }

      // Update email tags
      toEmails.value = settings.toEmail ? settings.toEmail.split(',').map(email => email.trim()) : []
      bccEmails.value = settings.bccEmail ? settings.bccEmail.split(',').map(email => email.trim()) : []

      settingsExist.value = true
      success.value = 'Settings loaded successfully'
      console.log('Settings loaded successfully')
    }
  } catch (err) {
    console.error('Error loading settings:', err)
    console.error('Error response:', err.response)
    
    if (err.response?.status === 404) {
      console.log('No settings found for machine ID:', formData.value.machine_id)
      // Reset form to default values when no settings found
      formData.value = {
        username: '',
        password: '',
        host: '',
        port: '22',
        folder_path: '/',
        machine_id: formData.value.machine_id, // Keep the machine ID
        to_email: '',
        start_date: '',
        is_ftp: '0',
        bcc_email: '',
        project_name: ''
      }
      
      // Reset email tags
      toEmails.value = []
      bccEmails.value = []
      emailInput.value = ''
      bccEmailInput.value = ''
      
      errors.value.machine_id = 'No settings found for this Machine ID'
      settingsExist.value = false
    } else {
      errors.value.machine_id = err.response?.data?.message || 'Failed to load settings'
    }
  } finally {
    isLoading.value = false
  }
}

const verifyFtpCredentials = async () => {
  if (!formData.value.host || !formData.value.port || !formData.value.username || !formData.value.password || !formData.value.folder_path) {
    alert('Please fill in all connection credentials (host, port, username, password, and folder path)')
    return
  }

  isVerifying.value = true
  try {
    const response = await axios.post(`${config.serverUrl}/api/gto/verify-ftp`, {
      host: formData.value.host,
      port: formData.value.port,
      username: formData.value.username,
      password: formData.value.password,
      folderPath: formData.value.folder_path,
      isFtp: formData.value.is_ftp === '1' // true for FTP, false for SFTP
    }, {
      headers: getAuthHeader()
    })
    
    const data = response.data
    
    if (!response.data.success) throw new Error(data.message || 'Failed to verify connection credentials')
    
    alert(data.message || 'Connection credentials verified successfully')
  } catch (error) {
    console.error('Error:', error)
    alert(error.response?.data?.message || 'Failed to verify connection credentials')
  } finally {
    isVerifying.value = false
  }
}

onMounted(async () => {
    await loadSettings()
  
})
</script>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  padding: 1.5rem;
  background-color: #f8f9fa;
}

.dashboard-header {
  background: white;
  padding: 1.25rem;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-section {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.dashboard-title {
  color: #333;
  font-size: 1.25rem;
  font-weight: 500;
  margin: 0;
  line-height: 1.2;
}

.dashboard-subtitle {
  color: #333;
  font-size: 1rem;
  font-weight: 400;
  margin: 0;
  line-height: 1.2;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.dashboard-card {
  background: white;
  padding: 1.5rem;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.settings-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

label {
  color: #333;
  font-weight: 400;
  font-size: 0.875rem;
}

input, select {
  padding: 0.375rem 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.875rem;
  height: 32px;
  background-color: white;
}

input::placeholder {
  color: #adb5bd;
}

input:focus, select:focus {
  outline: none;
  border-color: #3862EB;
  box-shadow: 0 0 0 1px rgba(56, 98, 235, 0.2);
}

.email-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.25rem;
}

.email-tag {
  background-color: #e9ecef;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.remove-tag {
  background: none;
  border: none;
  color: #868e96;
  cursor: pointer;
  padding: 0;
  font-size: 1rem;
  line-height: 1;
}

.remove-tag:hover {
  color: #dc3545;
}

.action-button {
  padding: 0.375rem 0.75rem;
  height: 36px;
  font-size: 0.875rem;
  font-weight: 400;
  border-radius: 20px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  min-width: 100px;
  white-space: nowrap;
}

.action-button.primary {
  background-color: #3862EB;
  border: 1px solid #3862EB;
  color: white;
}

.action-button.outline {
  background-color: white;
  border: 1px solid #3862EB;
  color: #3862EB;
  font-weight: 400;
}

.action-button:hover:not(:disabled) {
  opacity: 0.9;
}

.action-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .dashboard-header {
    flex-direction: column;
    gap: 1rem;
  }
  
  .header-actions {
    flex-wrap: wrap;
    width: 100%;
  }
  
  .action-button {
    flex: 1;
  }
}

.tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.tab-button {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  background: white;
  color: #495057;
  border: 1px solid #dee2e6;
}

.tab-button.active {
  background: #3862EB;
  color: white;
  border-color: #3862EB;
}

.tab-button.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #e9ecef;
  border-color: #dee2e6;
  color: #868e96;
}

.tab-button.disabled:hover {
  opacity: 0.5;
  background-color: #e9ecef;
  border-color: #dee2e6;
  color: #868e96;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.header-left {
  min-width: 200px;
}

.header-right {
  display: flex;
  gap: 0.75rem;
}

.project-select {
  width: 100%;
  min-width: 200px;
  height: 36px;
}

.historical-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-left {
    min-width: auto;
  }

  .header-right {
    flex-direction: column;
    gap: 0.5rem;
  }

  .project-select {
    width: 100%;
    min-width: auto;
  }
}

.password-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-container input {
  width: 100%;
  padding-right: 40px; /* Make room for the eye icon */
}

.toggle-password {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #495057;
  opacity: 0.6;
  transition: all 0.2s;
  width: 24px;
  height: 24px;
}

.toggle-password:hover {
  opacity: 1;
}

.toggle-password svg {
  width: 16px;
  height: 16px;
}

.ftp-container {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
}

.ftp-container select {
  flex: 1;
}

.verify-button {
  min-width: 100px;
  height: 32px;
  padding: 0 0.75rem;
  white-space: nowrap;
  border-radius: 20px;
}

@media (max-width: 768px) {
  .ftp-container {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .verify-button {
    width: 100%;
  }
}

select:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.navigation-links {
  display: flex;
  align-items: center;
}

.nav-link {
  padding: 8px 16px;
  background-color: #4a56e2;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 500;
  transition: background-color 0.3s;
}

.nav-link:hover {
  background-color: #3a46d2;
}

.header-action {
  margin-right: auto;
}

.back-link {
  margin-right: auto;
}

.back-button {
  background: none;
  border: none;
  color: #333;
  font-size: 0.875rem;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}

.back-button:hover {
  text-decoration: underline;
}
</style> 